import { createContext, useContext, type ReactNode } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { Check, ChevronDown, Info } from "lucide-react";
import { ReportTitle } from "../../molecules/page-elements.tsx";
import { formatPct, repsLabel, type GradeTone } from "./gradingStats.ts";
import { TONE_BOX, TONE_CELL, toneOf } from "./gradingReportData.ts";

/* ── Look ──────────────────────────────────────────────────────────────── */

/**
 * The reports' header — the Jersey Report's and Production Summary's own
 * (client 2026-10-20): switching between the four reports, the switcher, the
 * buttons and where the report starts don't move. The title is on paper
 * only (the switcher names the report on screen); a date sits small at the
 * left.
 */
export function ReportHeader({
  title,
  subtitle,
  printNote,
  status,
  menubar,
  actions,
}: {
  title: string;
  subtitle?: string;
  /** On paper only: what the pickers (hidden there) had chosen. */
  printNote?: string;
  /**
   * Loading while the figures stay up ("Updating…"): said here, beside the
   * date, where it moves nothing.
   */
  status?: string;
  menubar?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <ReportTitle
      title={title}
      menubar={menubar}
      leading={
        subtitle || printNote || status ? (
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-3">
              {subtitle ? <p className="truncate text-sm text-gray-500">{subtitle}</p> : null}
              {status ? (
                <span role="status" className="print-hide inline-flex min-w-0 items-center gap-1.5 text-xs text-gray-400">
                  <span className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                  <span className="truncate">{status}</span>
                </span>
              ) : null}
            </div>
            {printNote ? <p className="print-only text-sm text-gray-700">{printNote}</p> : null}
          </div>
        ) : null
      }
    >
      {actions}
    </ReportTitle>
  );
}

/** The Production Summary's game picker's look. */
const TRIGGER =
  "rounded-control inline-flex h-9 shrink-0 items-center gap-1.5 border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50";
const PANEL =
  "z-50 flex max-h-80 w-64 flex-col gap-1 overflow-y-auto rounded-md border border-gray-200 bg-white p-1 shadow-lg [--anchor-gap:4px] focus:outline-none";
const ROW = "flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-100";

export interface PickOption {
  key: string;
  label: string;
  /** Second line (a practice's date). */
  hint?: string;
}

/** One choice — a list the keyboard and screen readers know as one. */
export function ReportSelect({
  label,
  heading,
  options,
  value,
  onChange,
}: {
  /** What it picks, for screen readers ("Week"). */
  label: string;
  heading: string;
  options: PickOption[];
  value: string | null;
  onChange: (key: string) => void;
}) {
  const shown = options.find((o) => o.key === value)?.label ?? `Select ${label.toLowerCase()}`;
  return (
    <Listbox value={value} onChange={(key: string | null) => key != null && onChange(key)}>
      <div className="relative">
        <ListboxButton aria-label={`${label}: ${shown}`} className={TRIGGER}>
          <span className="max-w-[180px] truncate">{shown}</span>
          <ChevronDown size={14} className="shrink-0 text-slate-400" />
        </ListboxButton>
        <ListboxOptions anchor="bottom end" aria-label={heading} className={PANEL}>
          <div role="presentation" className="px-3 py-1 text-sm text-neutral-900">
            {heading}
          </div>
          {options.map((option) => (
            <ListboxOption key={option.key} value={option.key} className={`${ROW} data-[focus]:bg-gray-100`}>
              {({ selected }) => (
                <>
                  {selected ? (
                    <Check className="h-4 w-4 shrink-0 text-blue-600" />
                  ) : (
                    <span className="h-4 w-4 shrink-0" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{option.label}</span>
                    {option.hint ? <span className="block truncate text-xs text-gray-500">{option.hint}</span> : null}
                  </span>
                </>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}

/** Several choices; the list stays open while ticking. */
export function ReportMultiSelect({
  label,
  heading,
  options,
  selected,
  onChange,
  allLabel,
}: {
  label: string;
  heading: string;
  options: PickOption[];
  selected: readonly string[];
  onChange: (keys: string[]) => void;
  /** The trigger's text when every option is ticked. */
  allLabel: string;
}) {
  const picked = options.filter((o) => selected.includes(o.key));
  const shown =
    picked.length === options.length && options.length > 0
      ? allLabel
      : picked.length === 0
        ? `No ${label.toLowerCase()}`
        : picked.length === 1
          ? picked[0]!.label
          : `${picked[0]!.label} +${picked.length - 1}`;
  return (
    <Listbox value={picked.map((o) => o.key)} onChange={onChange} multiple>
      <div className="relative">
        <ListboxButton aria-label={`${label}: ${shown}`} className={TRIGGER}>
          <span className="max-w-[140px] truncate">{shown}</span>
          <ChevronDown size={14} className="shrink-0 text-slate-400" />
        </ListboxButton>
        <ListboxOptions anchor="bottom end" aria-label={heading} className={PANEL}>
          <div role="presentation" className="px-3 py-1 text-sm text-neutral-900">
            {heading}
          </div>
          {options.map((option) => (
            <ListboxOption key={option.key} value={option.key} className={`${ROW} data-[focus]:bg-gray-100`}>
              {({ selected: ticked }) => (
                <>
                  {ticked ? (
                    <Check className="h-4 w-4 shrink-0 text-blue-600" />
                  ) : (
                    <span className="h-4 w-4 shrink-0" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{option.label}</span>
                    {option.hint ? <span className="block truncate text-xs text-gray-500">{option.hint}</span> : null}
                  </span>
                </>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}

/** What a figure means: hover on a computer, tap on the iPad. */
function InfoTip({ label, info }: { label: string; info: string }) {
  return (
    <Popover className="relative inline-flex">
      <PopoverButton
        aria-label={`About ${label}`}
        title={info}
        className="print-hide inline-flex rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
      >
        <Info className="h-3.5 w-3.5 opacity-80" />
      </PopoverButton>
      <PopoverPanel
        anchor="bottom start"
        className="z-50 w-56 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-normal text-gray-700 shadow-lg [--anchor-gap:4px] focus:outline-none"
      >
        {info}
      </PopoverPanel>
    </Popover>
  );
}

/** The card a figure is in: its tip is "About Team Season", not one of many "About Season". */
const CardTitle = createContext<string | null>(null);

/** A figure in a summary card: label (with what it means), number, note. */
export function StatBox({
  label,
  info,
  value,
  note,
  tone = "none",
}: {
  label: string;
  info: string;
  value: string;
  note?: string;
  tone?: GradeTone | "none";
}) {
  const card = useContext(CardTitle);
  return (
    <div className={`min-w-0 flex-1 rounded-lg border px-4 py-3 ${TONE_BOX[tone]}`}>
      <div className="flex items-center gap-1.5 text-sm">
        <span>{label}</span>
        <InfoTip label={card ? `${card} ${label}` : label} info={info} />
      </div>
      {/* The reps note drops under the figure when the box is narrow. */}
      <div className="mt-1 flex flex-wrap items-baseline gap-x-1">
        <span className="whitespace-nowrap text-2xl font-bold">{value}</span>
        {note ? <span className="whitespace-nowrap text-xs">{note}</span> : null}
      </div>
    </div>
  );
}

/** A grade figure: coloured by the grade, with its reps beside it. */
export function GradeBox({ label, info, pct, reps }: { label: string; info: string; pct: number | null; reps: number }) {
  return (
    <StatBox
      label={label}
      info={info}
      value={formatPct(pct)}
      note={pct == null ? undefined : `(${repsLabel(reps)})`}
      tone={toneOf(pct)}
    />
  );
}

export function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="print-keep rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="mb-3 text-lg font-semibold text-gray-900">{title}</h2>
      <div className="flex gap-2">
        <CardTitle.Provider value={title}>{children}</CardTitle.Provider>
      </div>
    </section>
  );
}

/** The grade colour key. */
export function GradeLegend() {
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-xs font-semibold">
      <span className={`rounded px-2 py-0.5 ${TONE_CELL.good}`}>95%+</span>
      <span className={`rounded px-2 py-0.5 ${TONE_CELL.ok}`}>80–&lt;95</span>
      <span className={`rounded px-2 py-0.5 ${TONE_CELL.low}`}>&lt;80</span>
    </span>
  );
}

/** "Safeties", "Safeties and Tight Ends", "Safeties, Tight Ends and Wide Receivers". */
const andList = (names: readonly string[]): string =>
  names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/**
 * Loading, offline and failed notes for what comes from the server. Offline
 * and failed print too: paper must not pass off part of the team as all of it.
 */
export function LoadNote({
  loading,
  offline,
  hasData = false,
  failed,
  retrying = false,
  onRetry,
  ownUnit = false,
}: {
  loading: boolean;
  offline: boolean;
  /** Offline with what loaded before still in memory (and shown). */
  hasData?: boolean;
  /** The units that couldn't be loaded, by name. */
  failed: readonly string[];
  /** Those are being tried again: the note stays, saying so. */
  retrying?: boolean;
  onRetry: () => void;
  /** A position app's report: its own unit only. */
  ownUnit?: boolean;
}) {
  if (offline) {
    return (
      <p className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-600">
        {ownUnit
          ? hasData
            ? "You're offline. The saved practices are as last loaded; the practice on this iPad is up to date."
            : "The saved practices need an internet connection. Only the practice on this iPad is shown."
          : // The coordinator's: a reports app, nothing of its own on the iPad.
            hasData
            ? "You're offline. The units' practices are as last loaded."
            : "You're offline. The units' practices need an internet connection."}
      </p>
    );
  }
  if (failed.length > 0) {
    return (
      <p className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
        <span>
          {ownUnit
            ? "The saved practices couldn't be loaded. Only the practice on this iPad is shown."
            : `The ${andList(failed)} couldn't be loaded and are left out of the figures.`}
        </span>
        {retrying ? (
          <span role="status" className="print-hide inline-flex items-center gap-1.5 font-semibold">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
            Trying again…
          </span>
        ) : (
          <button type="button" onClick={onRetry} className="print-hide font-semibold underline">
            Try again
          </button>
        )}
      </p>
    );
  }
  if (loading) {
    return (
      <p role="status" className="print-hide mb-4 flex items-center gap-2 text-sm text-gray-500">
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        Loading practices…
      </p>
    );
  }
  return null;
}
